import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, ChevronDown, ChevronUp, Clock3, Loader2, MailWarning, PhoneOff, RefreshCw,
  Repeat, ShoppingCart, Stethoscope, UserRoundCheck,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { CLINIC_TIME_ZONE } from "@/lib/clinicTime";

const TYPE_META = {
  patient_late: {
    label: "Late",
    Icon: Clock3,
    tone: "rose",
  },
  patient_waiting_too_long: {
    label: "Waiting",
    Icon: UserRoundCheck,
    tone: "amber",
  },
  checkout_waiting: {
    label: "Checkout",
    Icon: ShoppingCart,
    tone: "violet",
  },
  doctor_split_across_chairs: {
    label: "Doctor split",
    Icon: Stethoscope,
    tone: "amber",
  },
  reschedule_pending: {
    label: "Reschedule",
    Icon: Repeat,
    tone: "sky",
  },
  email_bounced: {
    label: "Email bounced",
    Icon: MailWarning,
    tone: "rose",
  },
  no_phone: {
    label: "No phone",
    Icon: PhoneOff,
    tone: "slate",
  },
  unconfirmed_tomorrow: {
    label: "Unconfirmed",
    Icon: AlertTriangle,
    tone: "amber",
  },
};

const TONE_CLASSES = {
  amber: {
    badge: "bg-amber-100 text-amber-900",
    icon: "bg-amber-50 text-amber-700",
    border: "border-amber-200",
  },
  rose: {
    badge: "bg-rose-100 text-rose-900",
    icon: "bg-rose-50 text-rose-700",
    border: "border-rose-200",
  },
  sky: {
    badge: "bg-sky-100 text-sky-900",
    icon: "bg-sky-50 text-sky-700",
    border: "border-sky-200",
  },
  slate: {
    badge: "bg-slate-100 text-slate-700",
    icon: "bg-slate-50 text-slate-600",
    border: "border-slate-200",
  },
  violet: {
    badge: "bg-violet-100 text-violet-900",
    icon: "bg-violet-50 text-violet-700",
    border: "border-violet-200",
  },
};

export default function CalendarOpsQueuePanel({ branchId = null, onOpenAppointment }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    if (!branchId) {
      setPayload(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.rpc("list_calendar_ops_queue", {
      p_branch_id: branchId,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message || "Could not load Calendar Ops queue.");
      return;
    }
    setPayload(data || {});
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    return [...(payload?.queue || [])].sort((a, b) => {
      const priority = Number(a.priority || 999) - Number(b.priority || 999);
      if (priority !== 0) return priority;
      return String(a.scheduled_start || "").localeCompare(String(b.scheduled_start || ""));
    });
  }, [payload?.queue]);

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="calendar-ops-queue-panel">
      <div className={`flex flex-wrap items-start justify-between gap-3 px-3 py-3 ${collapsed ? "" : "border-b border-slate-100"}`}>
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h2 className="text-sm font-bold text-slate-950">Calendar Ops queue</h2>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">Needs attention now.</p>
        </div>
        <div className="flex items-center gap-2">
          <Metric label="Total" value={payload?.metrics?.total || rows.length || 0} />
          <Metric label="High" value={payload?.metrics?.high || 0} tone="rose" />
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
            aria-label={collapsed ? "Expand Calendar Ops queue" : "Collapse Calendar Ops queue"}
            aria-expanded={!collapsed}
            aria-controls="calendar-ops-queue-body"
            data-testid="calendar-ops-toggle"
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={load}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"
            aria-label="Refresh Calendar Ops queue"
            title="Refresh"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {!collapsed ? (
        <div id="calendar-ops-queue-body" className="divide-y divide-slate-100">
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-4 text-xs text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading queue...
            </div>
          ) : rows.length === 0 ? (
            <div className="px-3 py-4 text-xs font-medium text-slate-500" data-testid="calendar-ops-empty">
              Nothing needs attention.
            </div>
          ) : rows.map((row) => (
            <QueueRow key={row.key || `${row.item_type}:${row.appointment_id}`} row={row} onOpenAppointment={onOpenAppointment} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function QueueRow({ row, onOpenAppointment }) {
  const meta = TYPE_META[row.item_type] || {
    label: row.item_type || "Item",
    Icon: AlertTriangle,
    tone: "slate",
  };
  const tones = TONE_CLASSES[meta.tone] || TONE_CLASSES.slate;
  const Icon = meta.Icon;
  return (
    <article className={`grid gap-3 px-3 py-3 sm:grid-cols-[minmax(0,1fr)_auto] ${tones.border}`} data-testid={`calendar-ops-item-${row.appointment_id}`}>
      <div className="min-w-0">
        <div className="flex min-w-0 items-start gap-2">
          <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tones.icon}`}>
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${tones.badge}`}>
                {meta.label}
              </span>
              {row.severity ? (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                  {row.severity}
                </span>
              ) : null}
            </div>
            <p className="mt-1 truncate text-sm font-bold text-slate-950">{row.patient_name || "Unnamed patient"}</p>
            <p className="mt-0.5 text-xs font-semibold text-slate-700">{row.reason || meta.label}</p>
            {row.detail ? <p className="mt-0.5 text-xs text-slate-500">{row.detail}</p> : null}
            <p className="mt-1 text-[11px] text-slate-400">{formatWhen(row.scheduled_start)}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => onOpenAppointment?.(row.appointment, row)}
          data-testid={`calendar-ops-open-${row.appointment_id}`}
          className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
        >
          Open
        </button>
      </div>
    </article>
  );
}

function Metric({ label, value, tone = "slate" }) {
  const classes = tone === "rose"
    ? "border-rose-200 bg-rose-50 text-rose-800"
    : "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-bold ${classes}`}>
      <span className="tabular-nums">{value}</span>
      {label}
    </span>
  );
}

function formatWhen(value) {
  if (!value) return "";
  return new Date(value).toLocaleString(undefined, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZone: CLINIC_TIME_ZONE,
  });
}
